import React, { useEffect, useRef, useState } from "react";
import { Select } from "antd";
import { useSelector } from "react-redux";
import { postApi } from "../services/api";
import { SELECTED_CUSTOMER_STORAGE_KEY } from "../constant";

export default function AgentClientSelect({ selectedCustomerId, onClientChange }) {
  const { customer_id, company_name, is_agent } = useSelector((state) => state.auth);
  const [customerListLoading, setCustomerListLoading] = useState(false);
  const [allCustomers, setAllCustomers] = useState([{ customer_id, company_name }]);
  const customerSelectRef = useRef(null);

  useEffect(() => {
    if (!is_agent) return;

    const fetchCustomersForAgent = async () => {
      setCustomerListLoading(true);
      try {
        const response = await postApi("sales/agent/agent-customer-list", {
          agent_customer_id: customer_id,
        });

        const customers = response?.data || [];
        const hasSelf = customers.some((item) => String(item.customer_id) === String(customer_id));

        const normalizedCustomers = hasSelf
          ? customers
          : [
              {
                customer_id,
                company_name: company_name || "Self",
              },
              ...customers,
            ];

        setAllCustomers(normalizedCustomers);

        // Handle initial load / sync with session storage
        const stored = sessionStorage.getItem(SELECTED_CUSTOMER_STORAGE_KEY);
        const normalizedStored = stored === null || stored === "" ? null : Number.isNaN(Number(stored)) ? stored : Number(stored);
        const hasStoredInList = normalizedCustomers.some((item) => String(item.customer_id) === String(normalizedStored));
        const nextCustomerId = hasStoredInList ? normalizedStored : customer_id;

        // If the initialized ID differs from what the parent currently holds, trigger an update
        if (String(nextCustomerId) !== String(selectedCustomerId)) {
          onClientChange(nextCustomerId);
          sessionStorage.setItem(SELECTED_CUSTOMER_STORAGE_KEY, String(nextCustomerId));
        }
      } finally {
        setCustomerListLoading(false);
      }
    };

    fetchCustomersForAgent();
  }, [is_agent, customer_id, company_name]); // removed selectedCustomerId from dependencies to prevent loop

  // Don't render anything if the user isn't an agent
  if (is_agent !== 1) return null;

  return (
    <Select
      ref={customerSelectRef}
      placeholder="Select Client"
      className="!rounded-full w-[260px] !h-[42px] !px-4 !text-lg !border-2 !border-[#A33434] !bg-white !overflow-hidden [&_.ant-select-selection-search-input]:!text-center [&_.ant-select-selection-placeholder]:!text-center [&_.ant-select-selection-item]:!text-center"
      bordered={false}
      dropdownClassName="custom-dropdown-menu"
      onChange={(value) => {
        sessionStorage.setItem(SELECTED_CUSTOMER_STORAGE_KEY, String(value));
        onClientChange(value);
        customerSelectRef.current?.blur();
      }}
      value={allCustomers.some((customer) => String(customer.customer_id) === String(selectedCustomerId)) ? selectedCustomerId : undefined}
      loading={customerListLoading}
      optionLabelProp="children"
      showSearch
      filterOption={(input, option) =>
        String(option?.searchtext || "")
          .toLowerCase()
          .includes(input.toLowerCase())
      }
    >
      {allCustomers.map((customer) => (
        <Select.Option key={customer.customer_id} value={customer.customer_id} searchtext={customer.company_name}>
          <div className="truncate max-w-[200px]">{customer.company_name}</div>
        </Select.Option>
      ))}
    </Select>
  );
}
